const HOUR = 3600;
const MINUTE = 60;
const KILO_METER = 1000;

const formatCount = (value: number, unit: string = ''): string => {
  // 件数はカンマ区切りで表示する
  return `${Math.round(value).toLocaleString('en')}${unit}`;
};

const formatCountPerArea = (value: number, unit: string = ''): string => {
  if (value === 0) {
    return `0${unit}/k㎡`;
  }

  // 0.001未満、または1,000,000以上の数は指数表記で表示する
  if (Number(value.toPrecision(2)) < 0.001 || Math.round(value) >= 1000000) {
    return `${value.toExponential(1)}${unit}/k㎡`;
  }
  // 1未満の数は有効数字2桁で表示する
  if (value < 1) {
    return `${value.toPrecision(2)}${unit}/k㎡`;
  }
  // それ以外の数はカンマ区切りの整数で表示する
  return `${Math.round(value).toLocaleString('en')}${unit}/k㎡`;
};

const formatPercent = (value: number): string => {
  if (value > 0 && value.toFixed(1) === '0.0') {
    // 四捨五入して0.0%になる場合は `<0.1%` と表示する
    return '<0.1%';
  }
  return `${value.toFixed(1)}%`;
};

const formatDate = (value: Date | string): string => {
  const datetime = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(datetime.getTime())) {
    return '-';
  }

  const year = datetime.getFullYear();
  const month = String(datetime.getMonth() + 1);
  const day = String(datetime.getDate());

  return `${year}/${month}/${day}`;
};

const formatDateTime = (value: Date | string): string => {
  const datetime = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(datetime.getTime())) {
    return '-';
  }

  const year = datetime.getFullYear();
  const month = String(datetime.getMonth() + 1).padStart(2, '0');
  const day = String(datetime.getDate()).padStart(2, '0');
  const hour = String(datetime.getHours()).padStart(2, '0');
  const minute = String(datetime.getMinutes()).padStart(2, '0');

  return `${year}/${month}/${day} ${hour}:${minute}`;
};

const formatTime = (value: number): string => {
  // 時刻は `H:mm` 形式で表示する
  const hours = Math.floor(value / HOUR);
  const minutes = Math.floor((value % HOUR) / MINUTE);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const formatDuration = (value: number): string => {
  // 時間は分単位に四捨五入して `H'h'm'm'` 形式で表示する
  const roundedValue = Math.round(value / MINUTE) * MINUTE;
  const hours = Math.floor(roundedValue / HOUR);
  const minutes = Math.floor((roundedValue % HOUR) / MINUTE);
  return hours > 0
    ? `${hours}h${minutes > 0 ? `${minutes}m` : ''}`
    : `${minutes}m`;
};

const formatDistance = (value: number): string => {
  // 距離は四捨五入して1000m以上ならkm、それ以外はmで表示する
  const roundedValue = Math.round(value);
  return roundedValue >= KILO_METER
    ? `${(value / KILO_METER).toLocaleString('en', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}km`
    : `${roundedValue}m`;
};

const formatAny = (
  type: 'count' | 'countPerArea' | 'time' | 'duration' | 'distance' | 'percent',
  value: number,
  unit: string = ''
): string => {
  switch (type) {
    case 'count':
      return formatCount(value, unit);
    case 'countPerArea':
      return formatCountPerArea(value, unit);
    case 'time':
      return formatTime(value);
    case 'duration':
      return formatDuration(value);
    case 'distance':
      return formatDistance(value);
    case 'percent':
      return formatPercent(value);
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
};

export {
  formatCount,
  formatCountPerArea,
  formatPercent,
  formatDate,
  formatDateTime,
  formatTime,
  formatDuration,
  formatDistance,
  formatAny,
};
